import { Box, Button, Link, Typography, useMediaQuery } from '@mui/material';
import { Element } from 'react-scroll';
import CircularProgress from '@mui/material/CircularProgress';
import IUserMessage from '../../models/iUserMessage';
import IBotMessage from '../../models/iBotMessage';
import ExampleChatQuestions from './ExampleChatQuestions';
import { ExampleChatQuestion } from '../../pages/ChatPage';
import { useContext, useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { createUserConversations, queryGemini, queryLlm, updateUserConversations } from '../../providers/chatbotApi';
import { ResultInfo } from '../../types/ResultTypes';
import { toast } from 'react-toastify';
import ChatBubble from './ChatBubble';
import ResultsGrid from './ResultsGrid';
import ChatInput from '../inputs/ChatInput';
import ResultsFeedback from './ResultsFeedback';
import { MessageContext } from '../../context/MessageContext';
import MuiMarkdown from 'mui-markdown';
import { MobileConstants } from '../../constants';
import MultiTurn from './MultiTurn';
import { SearchTopic, SearchTopics } from '../../enum/SearchTopics';

export interface IChatProps {
  selectedOption: SearchTopic;
  setSelectedOption: (option: SearchTopic) => void;
  setSelectedData: (data: any) => void;
  setScrollToNewest: (scrollToNewest: boolean) => void;
  newChat: () => void;
  setForceOpenDetailTab: React.Dispatch<React.SetStateAction<number>>;
}

const exampleQuestions: ExampleChatQuestion[] = [
  {
    question:
      'What materials will last outdoors for more than 30 days, have static dissipative adhesion, and stick well to steel?',
    chatOption: SearchTopics.MaterialSearch,
  },
  {
    question:
      'What b-numbers come in yellow, can support a service temperature range of -10 to 150 Fahrenheit, and have engineering grade reflection?',
    chatOption: SearchTopics.MaterialSearch,
  },
  {
    question:
      'What materials are good for harsh environments, support a service temperature over 200 Fahrenheit, and have strong adhesion?',
    chatOption: SearchTopics.MaterialSearch,
  },
];

const maximumMaterialResultCount = 10;

const Chat = (props: IChatProps) => {
  const { authState } = useOktaAuth();
  const [productsToCompare, setProductsToCompare] = useState<string[][]>([]);
  const [showAllMaterials, setShowAllMaterials] = useState<boolean[]>([]);
  const isSmallScreen = useMediaQuery(MobileConstants.MOBILE_WIDTH_BREAKPOINT);
  const [mostRecentChatOption, setMostRecentChatOption] = useState<'material' | 'product'>();
  const [userQuery, setUserQuery] = useState<IUserMessage>();
  const {
    messages,
    setMessages,
    chatId,
    setChatId,
    setGeminiChat,
    setIsGeminiLoading,
    setIsLlmQueryLoading,
    isLlmQueryLoading,
  } = useContext(MessageContext);

  useEffect(() => {
    setProductsToCompare([]);
    setShowAllMaterials([]);
  }, [chatId]);

  const onViewDetailsClicked = (data: any) => {
    props.setSelectedData(data);
    props.setForceOpenDetailTab((prev) => prev + 1);
  };

  const onExampleButtonClicked = (question: ExampleChatQuestion) => {
    props.setSelectedOption(question.chatOption);
    handleNewMessage(question.question, 1, question.chatOption);
  };

  const handleNewMessage = async (
    message: string,
    current_turn: number,
    option: SearchTopic = props.selectedOption,
    parentId?: string,
    previous_results?: ResultInfo[]
  ) => {
    const userMessage = {
      text: message,
      isUser: true,
      minHeight: 'auto',
      parentId: parentId,
      turn: current_turn,
    } as IUserMessage;
    let updatedMessages = [...messages, userMessage];
    setUserQuery(userMessage);
    const isProduct = option === SearchTopics.ProductSearch;
    setMostRecentChatOption(isProduct ? 'product' : 'material');

    props.setScrollToNewest(true);
    setMessages(updatedMessages);
    setGeminiChat([{ text: message, isUser: true }]);
    setIsLlmQueryLoading(true);
    const accessToken = authState?.accessToken?.accessToken;
    if (accessToken) {
      try {
        handleGeminiQuery(accessToken, message);
        const apiResponse = await queryLlm(accessToken, message, option, current_turn, parentId, previous_results);
        const resultsList: ResultInfo[] = isProduct ? apiResponse.productInfoList : apiResponse.materialInfoList;
        updatedMessages = [
          ...updatedMessages,
          {
            text: (apiResponse.querySummary ? apiResponse.querySummary + '\n' : '') + apiResponse.text,
            isUser: false,
            results: resultsList,
            additionalData: apiResponse.additionalData,
            topic: apiResponse.topic,
            minHeight: 'auto',
            logId: apiResponse.logId,
            parentId: parentId,
            turn: current_turn,
            sqlQuery: apiResponse.sqlQuery,
          } as IBotMessage,
        ];
        setMessages(updatedMessages);
        setIsLlmQueryLoading(false);
        // Update the conversation history in background
        if (chatId) {
          await updateUserConversations(accessToken, chatId, updatedMessages);
        } else {
          const conversation = await createUserConversations(accessToken, updatedMessages);
          setChatId(conversation.id);
        }
      } catch (error) {
        console.error(error);
        let errorMessage = {
          text: 'An error occurred while fetching the response from the bot.',
          isUser: false,
          minHeight: 'auto',
        } as IBotMessage;
        setMessages([...updatedMessages, errorMessage]);
        toast.error('An error occurred while fetching the response from the bot.');
      }
    }
    setIsLlmQueryLoading(false);
  };

  /**
   * Handle Gemini seperately from regular chatbot queries
   * @param promise
   */
  const handleGeminiQuery = (accessToken: string, message: string) => {
    setIsGeminiLoading(true);
    queryGemini(accessToken, message)
      .then((geminiResponse) => {
        setGeminiChat((prev) => [
          ...prev,
          {
            text: geminiResponse.text,
            isUser: false,
            citations: geminiResponse.citations,
          },
        ]);
      })
      .catch((error) => {
        setGeminiChat((prev) => [...prev, { text: 'Error fetching Gemini response', isUser: false }]);
      })
      .finally(() => {
        setIsGeminiLoading(false);
      });
  };

  return (
    <Box className="flex-column" sx={{ flexGrow: 1, p: 3, marginRight: '0px' }}>
      <Box className="flex-column" sx={{ flexGrow: 1, height: '40vh' }}>
        {messages.length === 0 && (
          <ExampleChatQuestions
            questions={isSmallScreen ? [exampleQuestions[0]] : exampleQuestions}
            onExampleButtonClickedHandler={onExampleButtonClicked}
          />
        )}
        {messages.length > 0 && (
          <Box
            id="chatbox"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              mt: 2,
              mb: 2,
              flexGrow: 1,
              minHeight: '300px',
              overflowY: 'auto',
            }}
          >
            {messages.map((message, index) => {
              const resultsLength = message.isUser ? 0 : message.results?.length ?? 0;

              const createMessageName = (): string => {
                if (message.isUser) {
                  if (index === messages.length - 1 || index === messages.length - 2) {
                    return 'last-user-message';
                  }
                  if (index === 0) {
                    return 'first-user-message';
                  }
                }
                return 'other-message';
              };

              const showCompareButton = !message.isUser && message.topic === SearchTopics.ProductSearch;

              const handleCompare = () => {
                const url =
                  'https://www.bradyid.com/product-comparison?productIds=' +
                  productsToCompare[index].join('&productIds=');

                window.open(url, '_blank');
              };

              const showResultsExceededMessage =
                !message.isUser &&
                message.topic === SearchTopics.MaterialSearch &&
                !showAllMaterials[index] &&
                resultsLength > maximumMaterialResultCount;

              const showMaxMaterials = () => {
                setShowAllMaterials((prev) => {
                  const shown = [...prev];
                  shown[index] = true;
                  return shown;
                });
              };

              return (
                <Box
                  key={index}
                  sx={{
                    width: '100%',
                    minHeight: message.minHeight,
                    paddingRight: '20px',
                  }}
                >
                  <Element name={createMessageName()}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}
                    >
                      <ChatBubble
                        message={
                          showResultsExceededMessage ? (
                            <Box>
                              <MuiMarkdown
                                overrides={{
                                  ul: {
                                    component: 'ul',
                                    props: {
                                      style: {
                                        paddingLeft: '20px',
                                      },
                                    },
                                  },
                                }}
                              >
                                {message.text}
                              </MuiMarkdown>
                              <Typography
                                sx={{
                                  backgroundColor: '#fff59d',
                                  paddingTop: '2px',
                                  paddingX: '4px',
                                  borderTopLeftRadius: '4px',
                                  borderTopRightRadius: '4px',
                                }}
                              >
                                {`Your question returned more than ${maximumMaterialResultCount} results (${message.results?.length}).  We've displayed the first ${maximumMaterialResultCount} results below.`}
                              </Typography>
                              <Typography
                                sx={{
                                  backgroundColor: '#fff59d',
                                  paddingBottom: '2px',
                                  paddingX: '4px',
                                  borderBottomLeftRadius: '4px',
                                  borderBottomRightRadius: '4px',
                                }}
                              >
                                {'Try asking your question again and be more specific to narrow the results.'}
                              </Typography>
                              <Link
                                sx={{
                                  cursor: 'pointer',
                                }}
                                onClick={showMaxMaterials}
                              >
                                Show All Results
                              </Link>
                            </Box>
                          ) : (
                            message.text
                          )
                        }
                        isUser={message.isUser}
                        backgroundColor="#FFFFFF"
                      />
                      {showCompareButton && (
                        <Button disabled={(productsToCompare[index]?.length ?? []) < 1} onClick={handleCompare}>
                          Compare
                        </Button>
                      )}
                    </Box>
                  </Element>
                  {!message.isUser && resultsLength > 0 && (
                    <ResultsGrid
                      // Limit materials to maximumMaterialResultCount results, otherwise show all results
                      results={
                        (showResultsExceededMessage
                          ? message.results?.slice(0, maximumMaterialResultCount)
                          : message.results) ?? []
                      }
                      additionalData={message.additionalData}
                      topic={message.topic}
                      onClickViewDetailsHandler={onViewDetailsClicked}
                      productsToCompare={productsToCompare[index] ?? []}
                      setProductsToCompare={(products: string[]) => {
                        setProductsToCompare((prev) => {
                          let newProducts = [...prev];
                          newProducts[index] = products;
                          return newProducts;
                        });
                      }}
                    />
                  )}
                  {!message.isUser && (
                    <MultiTurn
                      logId={message.parentId === undefined ? message.logId : message.parentId}
                      results={message.results as ResultInfo[]}
                      topic={message.topic}
                      onSendMessage={handleNewMessage}
                      selectedOption={props.selectedOption}
                      turn={message.turn + 1}
                      disabled={isLlmQueryLoading}
                    />
                  )}
                  {!message.isUser && (
                    <ResultsFeedback
                      logId={message.logId}
                      userQuery={userQuery?.text}
                      llmResponse={message?.text}
                      topFiveResults={message.results?.slice(0, 5)}
                    />
                  )}
                </Box>
              );
            })}
            <Box sx={{ marginBottom: '100vh' }}>
              {isLlmQueryLoading && (
                <ChatBubble
                  message={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}
                    >
                      <CircularProgress />
                      <Box marginLeft={'5px'}>
                        Checking the {mostRecentChatOption === 'material' ? 'material' : 'product'} database
                      </Box>
                    </Box>
                  }
                  isUser={false}
                  backgroundColor="#FFFFFF"
                />
              )}
            </Box>
          </Box>
        )}
        <ChatInput
          onSendMessage={handleNewMessage}
          selectedOption={props.selectedOption}
          onSelectedOption={props.newChat}
          disabled={isLlmQueryLoading}
        />
      </Box>
    </Box>
  );
};

export default Chat;
